import { create } from 'zustand'

import type { AuthSession, User } from '@/api/types'
import {
  AUTH_STORAGE_KEY,
  clearPersistedAuth,
  readPersistedAuth,
  toPersisted,
  writePersistedAuth,
} from '@/auth/storage'

/**
 * `loading`       — session relue depuis le stockage, utilisateur pas encore revalidé
 * `authenticated` — jetons présents et `GET /auth/me` confirmé
 * `anonymous`     — aucune session
 */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export interface AuthState {
  status: AuthStatus
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  /** Ouvre une session (login ou acceptation d'invitation). */
  signIn: (session: AuthSession) => void
  /** Remplace les jetons après un rafraîchissement, sans toucher au statut. */
  setSession: (session: AuthSession) => void
  /** Met à jour l'utilisateur courant (profil, revalidation `/auth/me`). */
  setUser: (user: User) => void
  /** Vide la session locale. */
  signOut: () => void
  /** Marque la session comme confirmée ou anonyme après la revalidation initiale. */
  resolve: (status: Exclude<AuthStatus, 'loading'>) => void
}

const persisted = typeof window === 'undefined' ? null : readPersistedAuth()

export const useAuthStore = create<AuthState>((set, get) => ({
  status: persisted ? 'loading' : 'anonymous',
  accessToken: persisted?.access_token ?? null,
  refreshToken: persisted?.refresh_token ?? null,
  user: persisted?.user ?? null,

  signIn: (session) => {
    writePersistedAuth(toPersisted(session))
    set({
      status: 'authenticated',
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      user: session.user,
    })
  },

  setSession: (session) => {
    writePersistedAuth(toPersisted(session))
    set({
      accessToken: session.access_token,
      refreshToken: session.refresh_token,
      user: session.user,
      status: 'authenticated',
    })
  },

  setUser: (user) => {
    const { accessToken, refreshToken } = get()
    if (accessToken && refreshToken) {
      writePersistedAuth({ access_token: accessToken, refresh_token: refreshToken, user })
    }
    set({ user })
  },

  signOut: () => {
    clearPersistedAuth()
    set({ status: 'anonymous', accessToken: null, refreshToken: null, user: null })
  },

  resolve: (status) => {
    set({ status })
  },
}))

/** Synchronise les onglets : une déconnexion ailleurs se propage ici. */
export function subscribeToStorageSync(): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== AUTH_STORAGE_KEY) return
    const next = readPersistedAuth()
    if (!next) {
      useAuthStore.setState({
        status: 'anonymous',
        accessToken: null,
        refreshToken: null,
        user: null,
      })
      return
    }
    useAuthStore.setState({
      status: 'authenticated',
      accessToken: next.access_token,
      refreshToken: next.refresh_token,
      user: next.user,
    })
  }

  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener('storage', onStorage)
  }
}
